import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import AppShell, { PageContainer } from "@/components/AppShell";
import SeoHead from "@/components/SeoHead";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Bot,
  FileText,
  Flame,
  Lightbulb,
  ListChecks,
  RefreshCw,
  Sparkles,
  Target,
  TrendingUp,
  Trophy,
  Users,
} from "lucide-react";
import { toast } from "sonner";

export default function GameHubPage() {
  const { t, lang } = useT();
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const isDE = (lang || "de").startsWith("de");

  const loadGuide = useCallback(async (refresh = false) => {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await api.get(`/hub/${slug}/guide`, {
        params: { lang: isDE ? "de" : "en", refresh: refresh ? "true" : "false" },
      });
      setData(res.data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 404) {
        setNotFound(true);
      } else if (status === 429 && detail?.error === "refresh_rate_limited") {
        toast.error(isDE ? detail.message_de : detail.message_en);
      } else {
        toast.error(isDE ? "Hub konnte nicht geladen werden." : "Couldn't load hub.");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [slug, isDE]);

  useEffect(() => {
    setNotFound(false);
    setData(null);
    loadGuide(false);
  }, [loadGuide]);

  if (notFound) {
    return (
      <AppShell>
        <PageContainer>
          <Link to="/app" className="text-sm text-zinc-400 hover:text-neon inline-flex items-center gap-1.5 mb-6">
            <ArrowLeft size={14} /> {t("nav.home")}
          </Link>
          <div className="rounded-3xl border border-white/10 bg-zinc-900/50 p-10 text-center">
            <h2 className="font-display text-2xl font-bold">Hub not found</h2>
          </div>
        </PageContainer>
      </AppShell>
    );
  }

  const hub = data?.hub;
  const guide = data?.guide;
  const accent = hub?.accent || "#00E5FF";
  const updatedAgo = data?.updated_at ? formatRelative(data.updated_at, isDE) : null;

  return (
    <AppShell>
      <SeoHead kind="hub" id={slug} lang={isDE ? "de" : "en"} />
      <PageContainer className="pb-12">
        <Link
          to="/app"
          data-testid="hub-back"
          className="text-sm text-zinc-400 hover:text-neon inline-flex items-center gap-1.5 mb-4"
        >
          <ArrowLeft size={14} /> {isDE ? "Zurück" : "Back"}
        </Link>

        {hub ? (
          <div
            className="relative rounded-3xl overflow-hidden border border-white/5 bg-zinc-900/60 mb-6 animate-fade-up"
            data-testid="hub-hero"
          >
            <div className="relative h-56 sm:h-72 overflow-hidden">
              <img src={hub.cover} alt={hub.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent" />
              <div
                className="absolute inset-0"
                style={{ background: `linear-gradient(90deg, rgba(5,5,5,0.7), transparent 60%)` }}
              />
              <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-7">
                <span
                  className="text-[11px] uppercase tracking-[0.18em] font-mono inline-block"
                  style={{ color: accent }}
                >
                  {hub.tagline}
                </span>
                <h1
                  className="font-display text-3xl sm:text-5xl font-black tracking-tight mt-1"
                  data-testid="hub-title"
                >
                  {hub.name}
                </h1>
              </div>
            </div>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-6">
          <Link to={`/app/coach?game=${encodeURIComponent(hub?.name || slug)}`}>
            <Button
              className="rounded-full bg-neon text-black hover:bg-neon-hover font-bold h-11 px-5"
              data-testid="hub-coach-cta"
            >
              <Bot size={16} className="mr-2" /> {isDE ? "Coach fragen" : "Ask Coach"}
            </Button>
          </Link>
          <Link to={`/app/teammates?game=${encodeURIComponent(hub?.name || "")}`}>
            <Button
              variant="outline"
              className="rounded-full bg-transparent border-white/15 text-white hover:bg-white/5 hover:text-white h-11 px-5"
              data-testid="hub-squad-cta"
            >
              <Users size={16} className="mr-2" /> {isDE ? "Squad finden" : "Find Squad"}
            </Button>
          </Link>
          <div className="ml-auto flex items-center gap-3">
            {updatedAgo && (
              <span className="text-[11px] text-zinc-500 font-mono" data-testid="hub-updated-at">
                {isDE ? "Aktualisiert" : "Updated"}: {updatedAgo}
              </span>
            )}
            <Button
              variant="ghost"
              disabled={refreshing}
              onClick={() => loadGuide(true)}
              className="rounded-full text-zinc-300 hover:text-neon hover:bg-white/5 h-11 px-4"
              data-testid="hub-refresh"
            >
              <RefreshCw size={14} className={`mr-2 ${refreshing ? "animate-spin" : ""}`} />
              {isDE ? "Aktualisieren" : "Refresh"}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-white/5 bg-zinc-900/50 p-10 text-center text-zinc-400">
            {isDE ? "Live-Guide wird generiert…" : "Generating live guide…"}
          </div>
        ) : guide ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
            <Section
              title={isDE ? "Übersicht" : "Overview"}
              icon={Sparkles}
              testid="hub-overview"
              className="lg:col-span-2"
            >
              <p className="text-sm sm:text-base text-zinc-200 leading-relaxed">
                {guide.overview || "—"}
              </p>
            </Section>

            <Section title={isDE ? "Meta" : "Meta"} icon={Target} testid="hub-meta">
              <BulletList items={guide.meta} accent="neon" />
            </Section>

            <Section title={isDE ? "Patchnotes" : "Patch Notes"} icon={FileText} testid="hub-patchnotes">
              <BulletList items={guide.patchnotes} accent="cyan" />
            </Section>

            <Section title={isDE ? "Top Builds" : "Top Builds"} icon={Trophy} testid="hub-builds">
              {(guide.builds || []).length === 0 ? (
                <p className="text-sm text-zinc-500">—</p>
              ) : (
                <ul className="space-y-3">
                  {guide.builds.map((b, i) => (
                    <li key={i} className="rounded-xl bg-zinc-950/70 border border-white/5 p-3">
                      <div className="font-display font-bold text-neon">{b.name}</div>
                      <p className="text-sm text-zinc-300 mt-1">{b.note}</p>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section title={isDE ? "Tipps" : "Tips"} icon={Lightbulb} testid="hub-tips">
              <BulletList items={guide.tips} accent="amber" />
            </Section>

            <Section title={isDE ? "Strategie" : "Strategy"} icon={Flame} testid="hub-strategy">
              <BulletList items={guide.strategy} accent="rose" />
            </Section>

            <Section title={isDE ? "Challenges" : "Challenges"} icon={ListChecks} testid="hub-challenges">
              <BulletList items={guide.challenges} accent="neon" />
            </Section>

            <Section
              title={isDE ? "Trends" : "Trends"}
              icon={TrendingUp}
              testid="hub-trends"
              className="lg:col-span-2"
            >
              <BulletList items={guide.trends} accent="cyan" />
            </Section>
          </div>
        ) : null}
      </PageContainer>
    </AppShell>
  );
}

function Section({ title, icon: Icon, children, testid, className = "" }) {
  return (
    <section
      data-testid={testid}
      className={`rounded-2xl border border-white/5 bg-zinc-900/50 p-5 sm:p-6 animate-fade-up ${className}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="w-8 h-8 rounded-lg bg-neon/15 border border-neon/30 grid place-items-center text-neon">
          <Icon size={15} />
        </span>
        <h2 className="font-display text-base sm:text-lg font-bold">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function BulletList({ items, accent = "neon" }) {
  if (!items || items.length === 0) return <p className="text-sm text-zinc-500">—</p>;
  const dotCls = {
    neon: "bg-neon",
    cyan: "bg-cyan-400",
    rose: "bg-rose-400",
    amber: "bg-amber-400",
  }[accent] || "bg-neon";
  return (
    <ul className="space-y-2.5">
      {items.map((s, i) => (
        <li key={i} className="flex gap-3 items-start">
          <span className={`mt-2 w-1.5 h-1.5 rounded-full shrink-0 ${dotCls}`} />
          <p className="text-sm text-zinc-200 leading-relaxed">
            {typeof s === "string" ? s : s?.text || JSON.stringify(s)}
          </p>
        </li>
      ))}
    </ul>
  );
}

function formatRelative(iso, isDE) {
  if (!iso) return null;
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return isDE ? "gerade eben" : "just now";
    if (diff < 3600) {
      const m = Math.floor(diff / 60);
      return isDE ? `vor ${m} Min` : `${m} min ago`;
    }
    if (diff < 86400) {
      const h = Math.floor(diff / 3600);
      return isDE ? `vor ${h} Std` : `${h} h ago`;
    }
    const d = Math.floor(diff / 86400);
    return isDE ? `vor ${d} Tg` : `${d} d ago`;
  } catch (_e) {
    return null;
  }
}